import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import noop from 'lodash/noop';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import kebabCase from 'lodash/kebabCase';
import { ButtonWithIcon } from '@els/els-react--button';
import { Flyout } from '@els/els-react--flyout';
import { Loader } from '@els/els-react--loader';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import ReactReactionDetailsHelper from '@Components/Results/services/react-reaction-details-helper.service';
import UserDatabases from '@Services/UserDatabases';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import HoverTooltip from '@Components/HoverTooltip';
import { TooltipConstants } from '@Components/Tooltip';
import Catalyst from '@Services/Catalyst';
import Similarity from '@Services/Similarity';
import NumberFormatter from '@Utils/NumberFormatter';
import { useHistory } from 'react-router-dom';
import { createProjectFromReaxysReaction } from '@Pages/Conditions/ConditionsService';
import { goToCreatedConditionsProject } from '@Pages/Conditions/navigation';
import { trackResultsExploreConditions } from '@Pages/Conditions/analytics';
import ResultHitNavigation from '@Components/Results/common/ResultHitNavigation';

function ReactionTopButtons({
  metadata,
  reaction,
  index,
  toggleHitDetailsOpen,
  isHitDetailsOpen,
  isAllDetailsOpen,
  toggleAllDetailsOpen,
  isFindSimilarOpen,
  toggleFindSimilarOpen,
  topButtonsRef,
}) {
  const history = useHistory();
  const [isCreatingConditions, setIsCreatingConditions] = useState(false);
  const isResultsPage = !metadata?.isViewPreview && !metadata?.fromDocuments && !metadata?.fromRT;

  function isHandledByGenericErrorFlow(error) {
    return Boolean(error?.status || error?.statusCode || error?.component);
  }
  const findSimilarConditions = async () => {
    if (isCreatingConditions) return;

    trackResultsExploreConditions();

    if (!reaction.rxnFile) {
      NotifyDialogsService.openModalWindow({
        type: 'error',
        title: 'Condition search failed',
        text: 'A reaction structure is not available for this reaction.',
        confirmText: 'Close',
      });
      return;
    }

    setIsCreatingConditions(true);
    try {
      const project = await createProjectFromReaxysReaction({ reactionId: reaction.id, rxnFile: reaction.rxnFile });
      goToCreatedConditionsProject(history, project);
    } catch (error) {
      Logger.error('ReactionTopButtons explore conditions failed with error:', error);
      if (!isHandledByGenericErrorFlow(error)) {
        NotifyDialogsService.openModalWindow({
          type: 'error',
          title: 'Condition search failed',
          text: error?.message || 'Condition search could not be created.',
          confirmText: 'Close',
        });
      }
      setIsCreatingConditions(false);
    }
  };

  const findSimilarReactions = () => {
    const databaseId = reaction.databaseId || metadata.databaseId;
    const { hitSetKey } = metadata;
    if (isResultsPage) {
      toggleFindSimilarOpen?.();
    } else {
      Similarity.showModalForReaction(reaction.id, databaseId, hitSetKey, reaction.rxnFile);
    }
  };

  const onReactionLinkClick = (link) => {
    const dbName = UserDatabases.getDbName(get(metadata, 'databaseId'));
    const linkName = link.label;
    const linkType = `menu:${dbName}`;
    const actionName = `rx2:openindatabase:${link.label}`;

    Catalyst.trackContentLinkClick(linkName, linkType, actionName);

    ReactReactionDetailsHelper.goToContext('reactions', link, metadata);
  };

  const getReactionLinks = () => (
    <ul>
      {!isEmpty(reaction.reactionLinks)
        ? reaction?.reactionLinks?.map((link) => (
            <ButtonWithIcon
              data-e2e="reaction-open-in-database-link"
              key={link.databaseId}
              sprite="ChevronRight"
              type="link"
              linkIconSize="xs"
              className={classnames('e2e-drop-item', `e2e-${kebabCase(link.label)}`)}
              onClick={() => onReactionLinkClick(link)}>
              {link.label}
            </ButtonWithIcon>
          ))
        : null}
    </ul>
  );

  const isSimilarityBtnDisabled = !get(reaction, 'isSimilaritySearchAvailable', get(metadata, 'isSimilaritySearchAvailable'));
  // Find Similar is a Results-page action. Document-nested reactions and
  // non-results surfaces keep their existing navigation unchanged.
  const similarityBtnDisabledTooltip = isSimilarityBtnDisabled ? 'No similar reactions available' : '';
  const tooltipId = TooltipConstants.ids.GlobalHover;

  return (
    <div ref={topButtonsRef} className="u-els-margin-bottom-1x1o2 u-els-margin-top-1o2 u-els-display-flex">
      <Loader isVisible={isCreatingConditions} isBlocking={false} />
      <ResultHitNavigation
        className="reaction-top-buttons-navigation"
        ariaLabel={reaction.id ? `Reaction result ${reaction.id} details` : 'Reaction result details'}>
        {reaction.hitDetailsCount > 0 && (
          <ButtonWithIcon
            data-e2e="reaction-hits"
            type="link"
            linkIconSize="xs"
            sprite={isHitDetailsOpen ? 'ChevronUp' : 'ChevronDown'}
            iconRight
            className={`${isHitDetailsOpen ? ' active' : ''} u-els-margin-right e2e-reaction-hits`}
            onClick={toggleHitDetailsOpen}
            aria-expanded={isHitDetailsOpen}
            aria-controls={`reaction-hit-details-${index}`}
            data-click-delegation
            data-tracking={!isHitDetailsOpen}
            data-tracking-type="contentlinkclick"
            data-tracking-action={reaction.hitDetailsCount !== reaction.detailsCount ? 'Hits' : 'Hits/Conditions'}>
            <span>{reaction.hitDetailsCount !== reaction.detailsCount ? 'Hits' : 'Hits/Conditions'}</span>
            <span> - {NumberFormatter(reaction.hitDetailsCount)}</span>
          </ButtonWithIcon>
        )}
        {reaction.detailsCount > 0 && reaction.hitDetailsCount !== reaction.detailsCount && (
          <ButtonWithIcon
            data-e2e="reaction-conditions"
            type="link"
            linkIconSize="xs"
            sprite={isAllDetailsOpen ? 'ChevronUp' : 'ChevronDown'}
            iconRight
            className={`${isAllDetailsOpen ? ' active' : ''} u-els-margin-right e2e-reaction-conditions`}
            onClick={toggleAllDetailsOpen}
            aria-expanded={isAllDetailsOpen}
            aria-controls={`reaction-details-${index}`}
            data-click-delegation
            data-tracking={!isAllDetailsOpen}
            data-tracking-type="contentlinkclick"
            data-tracking-action="Conditions">
            <span>Conditions</span>
            <span> - {NumberFormatter(reaction.detailsCount)}</span>
          </ButtonWithIcon>
        )}
        {(!isResultsPage || !isSimilarityBtnDisabled) && (
          <HoverTooltip id={tooltipId} tooltip={similarityBtnDisabledTooltip} className="shopping-cart-tooltip" theme="simple">
            <ButtonWithIcon
              data-e2e="reaction-find-similar"
              type="link"
              className={`${isFindSimilarOpen ? ' active' : ''} u-els-margin-right e2e-reaction-find-similar`}
              disabled={isSimilarityBtnDisabled}
              onClick={findSimilarReactions}
              sprite={isResultsPage && isFindSimilarOpen ? 'ChevronUp' : 'ChevronRight'}
              iconRight
              linkIconSize="xs"
              aria-expanded={isResultsPage ? isFindSimilarOpen : undefined}
              aria-controls={isResultsPage ? `reaction-find-similar-${index}` : undefined}>
              Find Similar
            </ButtonWithIcon>
          </HoverTooltip>
        )}

        <ButtonWithIcon
          data-e2e="reaction-similar-conditions"
          type="link"
          linkIconSize="xs"
          className="u-els-margin-right e2e-reaction-similar-conditions"
          disabled={isCreatingConditions}
          onClick={findSimilarConditions}
          sprite="ChevronRight"
          iconRight>
          Explore conditions
        </ButtonWithIcon>

        <span className="u-els-line-height-default u-els-margin-right">Reaction ID: {reaction.id}</span>

        {reaction.reactionLinks && (
          <Flyout
            className="rx-reaction-open-in-database"
            flyout={
              <div className="u-els-font-size-base u-els-color-text u-els-margin-right-2x">
                <div className="u-els-margin-bottom u-els-font-size-h4 u-els-nowrap">Open in database</div>
                {getReactionLinks()}
              </div>
            }
            shouldCloseOnESC
            showCloseButton
            placement="bottom-end"
            pointerType="primary">
            <ButtonWithIcon
              data-e2e="reaction-open-in-database"
              sprite="ChevronDown"
              type="link"
              linkIconSize="xs"
              iconRight
              onClick={noop}
              className="e2e-reaction-open-in-database">
              Open in database
            </ButtonWithIcon>
          </Flyout>
        )}
      </ResultHitNavigation>
    </div>
  );
}

ReactionTopButtons.propTypes = {
  metadata: MetadataPropTypesShape,
  reaction: PropTypes.object.isRequired,
  index: PropTypes.string,
  isHitDetailsOpen: PropTypes.bool,
  isAllDetailsOpen: PropTypes.bool,
  toggleHitDetailsOpen: PropTypes.func.isRequired,
  toggleAllDetailsOpen: PropTypes.func.isRequired,
  isFindSimilarOpen: PropTypes.bool,
  toggleFindSimilarOpen: PropTypes.func,
  // Ref to the toggle-button container, owned by the parent to restore focus on close.
  topButtonsRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
};

export default ReactionTopButtons;
